// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React from "react"
import { BarDatum, ComputedDatum } from "@nivo/bar"
import moment from "moment"
import { GlobalFilterObject } from "@moodys/cre-cpm.models.global-filters"
import {
  DashboardFilters,
  ScatterPlotModel,
  ScatterPlotValue,
  Range
} from "@moodys/cre-cpm-client-apis.apis.services.platform-dashboards"

import { hexToRGB } from "@moodys/cre-cpm.functions.conversion"
import { DynamicLayout } from "@moodys/cre-cpm-client-apis.apis.services.auth"
import { dashboardStructure } from "@moodys/cre-cpm-platform-shared.constants.dashboard"

export const getChartFiltersToApply: (
  entity: keyof DashboardFilters | null,
  attribute: string,
  filters: DashboardFilters,
  assetId?: string
) => DashboardFilters = (entity, attribute, filters, assetId) => {
  const filterParams: DashboardFilters = { ...filters }

  // We add the assetId if we have one
  if (assetId) {
    filterParams.asset = { ...filterParams.asset, asset_ref_uuid: assetId }
  }

  // This validation is in the case of the attribute is already on the appliedFilters, it happens
  // when we have two charts with the same filter name
  if (entity) {
    if (Object.keys(filterParams[entity] || {}).includes(attribute)) {
      return {
        ...filterParams,
        [entity]: {
          // @ts-ignore
          ...filterParams[entity]
        }
      }
    }

    return {
      ...filterParams,
      [entity]: {
        // @ts-ignore
        ...filterParams[entity],
        [attribute]: []
      }
    }
  }

  // This validation is in the case of the attribute is already on the appliedFilters, it happens
  // when we have two charts with the same filter name
  if (Object.keys(filterParams).includes(attribute)) {
    return {
      ...filterParams
    }
  }

  return {
    ...filterParams,
    [attribute]: []
  }
}

export const filterFormatFunctionToUnixTimestamp = (min: number, max: number): [number, number] => [
  moment([min]).unix(),
  moment([max]).endOf("year").unix()
]

export const getBarChartFormattedValue: (
  key: string,
  filterFormatFunction?: (min: number, max: number) => [number, number]
) => { min: number; max: number } = (
  key,
  filterFormatFunction = (min, max): [number, number] => [min, max]
) => {
  const isRange = key.includes("-")
  if (isRange) {
    const [min, max] = key.split("-").map((item) => parseInt(item, 10))
    return { min, max }
  }

  const isLessThan = key.includes("<")
  const isMoreThan = key.includes(">")
  let min = parseInt(key.replace(/\D/g, ""), 10)
  let max = min

  if (isLessThan) {
    min = 0
    max -= 1
  } else if (isMoreThan) {
    min += 1
    max = 9999
  }

  ;[min, max] = filterFormatFunction(min, max)

  return { min, max }
}

export const getStringBarChartColors = (
  value: ComputedDatum<BarDatum> | null,
  appliedFilters: string[]
): string => {
  const appliedFiltersHasSomeValue =
    value &&
    appliedFilters.some((item: string) => item === value.data.key || item === value.data.value)

  if (!appliedFilters.length || (appliedFilters.length && appliedFiltersHasSomeValue)) {
    return "var(--primary-color)"
  }

  return "var(--empty-chart-color)"
}

export const getRangeBarChartColors = (
  value: ComputedDatum<BarDatum>,
  appliedFilters: Range[]
): string => {
  const thisFilter = getBarChartFormattedValue(
    value.data.key as string,
    filterFormatFunctionToUnixTimestamp
  )

  const appliedFiltersHasSomeValue = appliedFilters.some(
    (item: Range) =>
      (item as Range).min === thisFilter.min && (item as Range).max === thisFilter.max
  )

  if (!appliedFilters.length || (appliedFilters.length && appliedFiltersHasSomeValue)) {
    return "var(--primary-color)"
  }

  return "var(--empty-chart-color)"
}

export const getColorInRange = (percentage: number): string => {
  const companyColor = getComputedStyle(document.documentElement)
    .getPropertyValue("--primary-color")
    .toUpperCase()
  return hexToRGB(companyColor, percentage)
}

export const updateFiltersWithRange = (
  newFilter: { min: number; max: number },
  appliedFilters: Range[]
): Range[] => {
  let filtersToApply: Range[]

  if (
    appliedFilters.some(
      (item) => newFilter.min === (item as Range).min && newFilter.max === (item as Range).max
    )
  ) {
    filtersToApply = appliedFilters.filter(
      (item: Range | string) =>
        (item as Range).min !== newFilter.min && (item as Range).max !== newFilter.max
    )
  } else {
    filtersToApply = [...appliedFilters, newFilter]
  }

  return filtersToApply
}

const createFiltersToApply = (filterValue: string, appliedFilters: string[]): string[] => {
  let filtersToApply: string[]

  if (appliedFilters.includes(filterValue)) {
    filtersToApply = appliedFilters.filter((item: string) => item !== filterValue)
  } else {
    filtersToApply = [...appliedFilters, filterValue]
  }

  return filtersToApply
}

export const updateFiltersWithString = (
  value: ComputedDatum<BarDatum>,
  appliedFilters: string[]
): string[] => {
  const newFilter = value.data.key as string
  return createFiltersToApply(newFilter, appliedFilters)
}

export const updateFiltersScatterplotChart = (
  newFilter: string,
  appliedFilters: string[]
): string[] => {
  return createFiltersToApply(newFilter, appliedFilters)
}

export const shouldMakeChartRequest = (
  lastUpdatedFilter: string | null,
  filterName: string,
  globalFilters?: GlobalFilterObject[] | null,
  assetId?: string
): boolean => !lastUpdatedFilter?.includes(filterName) && !!(globalFilters || assetId)

export const getScatterPlotSeries: (rawData: ScatterPlotModel) => ScatterPlotValue[] = (
  rawData
) => {
  if (rawData?.attributes.values.length) {
    const minSize = rawData.attributes.values.reduce(
      (total, item) => Math.min(total, item.attributes.size),
      rawData.attributes.values[0].attributes.size
    )
    const maxSize = rawData.attributes.values.reduce(
      (total, item) => Math.max(total, item.attributes.size),
      rawData.attributes.values[0].attributes.size
    )
    return rawData.attributes.values.map((bubble) => ({
      key: bubble.attributes.value || "",
      x: bubble.attributes.x,
      y: bubble.attributes.y,
      size: 9 + (50 * (bubble.attributes.size - minSize)) / (maxSize - minSize),
      name: bubble.attributes.name,
      extra: bubble.attributes.extra
    }))
  }
  return []
}

export const dashboardChartSpan = (chartId: string): number => {
  switch (chartId) {
    case "default_kpis":
    case "deal_stage_id_total":
    case "deal_stage_id_volume":
    case "tenant_description":
    case "risks_kpis":
    case "latest_year_vs_trigger":
    case "implied_rating_change":
      return 24
    case "assets_country_code":
    case "status_date_total":
    case "status_date_volume":
      return 16
    default:
      return 8
  }
}

export const populateDynamicLayouts = (layouts: DynamicLayout | null): DynamicLayout =>
  layouts
    ? {
        asset_dashboard: {
          ...(layouts.asset_dashboard || dashboardStructure.asset_dashboard),
          multifamily:
            layouts.asset_dashboard.multifamily || dashboardStructure.asset_dashboard.multifamily
        },
        portfolio_dashboard: {
          ...(layouts.portfolio_dashboard || dashboardStructure.portfolio_dashboard),
          multifamily:
            layouts.portfolio_dashboard?.multifamily ||
            dashboardStructure.portfolio_dashboard?.multifamily
        },
        deals_dashboard: {
          ...(layouts.deals_dashboard || dashboardStructure.deals_dashboard)
        },
        tenants: {
          tenants_dashboard:
            layouts.tenants?.tenants_dashboard ||
            dashboardStructure.tenants?.tenants_dashboard ||
            [],
          tenants_list:
            layouts.tenants?.tenants_list || dashboardStructure.tenants?.tenants_list || []
        }
      }
    : dashboardStructure
